import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import enResource from "./en";
import esResource from "./es";
import ptResource from "./pt";
import {
  format as formatDate,
  formatDistance,
  formatRelative,
  isDate,
} from "date-fns";
import { capitalizeFirstLetter, getLanguageFormatted } from "../utils/string";
import {
  APP_SUPPORTED_LANGUAGES,
  I18NEXT_STORAGE_KEY,
  getAppLocale,
  getDateFnsLocale,
} from "../utils/i18nLanguage";

const syncDocumentLanguage = (language) => {
  document.documentElement.lang = getLanguageFormatted(language);
};

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    fallbackLng: "en",
    supportedLngs: APP_SUPPORTED_LANGUAGES,
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    debug: import.meta.env.VITE_IS_STAGING === "false" ? false : true,
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: I18NEXT_STORAGE_KEY,
    },
    resources: APP_SUPPORTED_LANGUAGES.reduce((resources, code) => {
      const resourceByCode = { en: enResource, es: esResource, pt: ptResource };
      if (!resourceByCode[code]) {
        throw new Error(`Missing i18n resource for ${code}`);
      }
      resources[code] = resourceByCode[code];
      return resources;
    }, {}),
    interpolation: {
      escapeValue: false,
      format: (value, format, lng, edit) => {
        const appLocale = getAppLocale(lng);
        const locale = getDateFnsLocale(lng);

        if (isDate(value)) {
          if (format === "hour")
            return formatDate(value, "hh:mm aaaa", { locale });
          if (format === "day") return formatDate(value, "dd", { locale });
          if (format === "dayShort") return formatDate(value, "d", { locale });
          if (format === "dayText") return formatDate(value, "eee", { locale });
          if (format === "dayTextLong")
            return formatDate(value, "eeee", { locale });
          if (format === "month") return formatDate(value, "MM", { locale });
          if (format === "monthText")
            return capitalizeFirstLetter(formatDate(value, "MMMM", { locale }));
          if (format === "monthTextShort")
            return capitalizeFirstLetter(formatDate(value, "MMM", { locale }));
          if (format === "year") return formatDate(value, "yyyy", { locale });
          if (format === "short") return formatDate(value, "PPP", { locale });
          if (format === "shortWithoutYear")
            return capitalizeFirstLetter(
              formatDate(value, "MMMM d", { locale })
            );
          if (format === "long") return formatDate(value, "Pp", { locale });
          if (format === "relative")
            return formatRelative(value, new Date(), { locale });
          if (format === "ago")
            return formatDistance(value, new Date(), {
              locale,
              addSuffix: true,
            });

          return formatDate(value, format, { locale });
        }

        if (typeof value === "number" || typeof Number(value) === "number") {
          if (format === "number" || !edit.currency) {
            return Number(value).toLocaleString(appLocale.intl, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              useGrouping: true,
            });
          }

          const formattedCurrency = Number(value).toLocaleString(
            appLocale.intl,
            {
              style: "currency",
              currency: edit.currency,
              currencyDisplay: appLocale.currencyDisplay,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              useGrouping: true,
            }
          );
          return `${appLocale.currencyPrefix}${formattedCurrency}`;
        }

        return value;
      },
    },
  });

i18next.on("initialized", () => {
  syncDocumentLanguage(i18next.language);
});

i18next.on("languageChanged", (language) => {
  syncDocumentLanguage(language);
});
